import React from "react";
import LegalLayout from "@/components/LegalLayout";

export default function CookieNotice() {
  return (
    <LegalLayout
      slug="cookies"
      eyebrow="Cookies"
      title="Cookie & Tracking Notice"
      lastUpdated="May 26, 2026 · Reviewed by counsel"
    >
      <p>
        Armstrong K9 Registry uses cookies and similar
        browser-storage technologies (localStorage, sessionStorage, IndexedDB)
        to operate the platform, remember your preferences, secure your
        session, and measure how visitors use the site. This Notice explains
        what we set, why, how long they persist, and how you can disable them.
      </p>

      <h2>1. Categories of cookies we use</h2>

      <h3>1.1 Strictly necessary (cannot be disabled)</h3>
      <ul>
        <li>
          <strong>session_token / access_token</strong> — keeps you signed in.
          7-day expiry, HTTP-only where possible, SameSite=Lax.
        </li>
        <li>
          <strong>cookie_consent</strong> — remembers your choice from the
          privacy banner so we do not re-prompt on every page load.
        </li>
        <li>
          <strong>cf_*</strong> — Cloudflare bot-protection challenge tokens
          (Turnstile). Required for sign-in, registration, and password reset.
        </li>
        <li>
          <strong>__stripe_*</strong> — Stripe fraud-prevention tokens set on
          checkout pages only. Required to process any payment.
        </li>
      </ul>

      <h3>1.2 Functional (set when you opt in)</h3>
      <ul>
        <li>
          <strong>onboarding_*</strong> — local progress markers for the new-
          user setup guide, so we do not re-show a step you already completed.
        </li>
        <li>
          <strong>persona_pref</strong> — your last-viewed dashboard mode
          (breeder / buyer / transporter / dog owner) so the next visit lands
          on the surface you actually use.
        </li>
        <li>
          <strong>push_subscription</strong> — endpoint URL + per-device label
          stored if you grant browser push permission for calendar reminders.
          Revoke from Settings → Notifications.
        </li>
      </ul>

      <h3>1.3 Analytics (set only with consent)</h3>
      <ul>
        <li>
          <strong>posthog_*</strong> — anonymous product-usage analytics so we
          know which features are used, which ones break, and which onboarding
          steps people abandon. We do <em>not</em> sell this data.
        </li>
        <li>
          <strong>sentry_*</strong> — anonymous error-tracking so we can fix
          bugs you hit. Captures the URL, browser version, and stack trace —
          never form values.
        </li>
      </ul>

      <h3>1.4 What we do NOT use</h3>
      <ul>
        <li>No advertising cookies. No remarketing pixels.</li>
        <li>No third-party social-network trackers.</li>
        <li>No cross-site tracking. Your behaviour on this site is not sold or shared with data brokers.</li>
      </ul>

      <h2>2. Local storage and other browser storage</h2>
      <p>
        In addition to cookies we use <code>localStorage</code> for the
        authentication token (JWT) and small preference flags
        (theme, last-selected dog, etc.). We use <code>sessionStorage</code>
        for transient form-state recovery. We do not use IndexedDB.
      </p>

      <h2>3. Managing your preferences</h2>
      <ul>
        <li>
          <strong>Cookie banner.</strong> First visit shows an "Accept all /
          Essential only" choice. We honor it.
        </li>
        <li>
          <strong>Browser settings.</strong> Every modern browser lets you
          block or delete cookies — see your browser's privacy settings.
          Blocking strictly-necessary cookies will break sign-in and payments.
        </li>
        <li>
          <strong>Push notifications.</strong> Revoke per-device from
          Settings → Notifications, or via your browser's site permissions.
        </li>
        <li>
          <strong>Do Not Track.</strong> Browsers send a DNT header. When set,
          we do not load analytics cookies even if you previously accepted them.
        </li>
      </ul>

      <h2>4. Sub-processors that set cookies</h2>
      <ul>
        <li><strong>Cloudflare</strong> — bot protection (Turnstile)</li>
        <li><strong>Stripe</strong> — payment fraud prevention</li>
        <li><strong>PostHog</strong> — anonymous product analytics</li>
        <li><strong>Sentry</strong> — anonymous error tracking</li>
        <li><strong>Daily.co</strong> — set only when you join a live video tour</li>
      </ul>
      <p>
        See our <a href="/sub-processors">Sub-processors</a> page for the full
        list, jurisdictions, and DPA links.
      </p>

      <h2>5. Updates to this Notice</h2>
      <p>
        Material changes will be announced on this page with an updated
        "Last updated" date and (for substantive changes) an in-app banner
        on your next sign-in.
      </p>

      <h2>6. Contact</h2>
      <p>
        Privacy questions: <a href="mailto:privacy@armstrongk9registry.com">privacy@armstrongk9registry.com</a> or
        use our <a href="/contact">Contact Us</a> form.
      </p>
    </LegalLayout>
  );
}
